import storage from 'azure-storage'
import uuid from 'uuid/v4'
import fileType from 'file-type'

import {
  forOwn,
  cloneDeep,
  mapValues,
  isUndefined,
} from 'lodash'


let svc, svcBlob, eg, table, partition, schema, limit, blobContainer


// --- helpers

// get entities into a simpler internal format
const deazurify = (entity) => {
  // remove metadata and coerce any Int64s to JS numbers
  const cleaned = mapValues(entity, (v, name) => {
    if((schema[name] || {}).type === 'number')
      return Number(v._)
    return v._
  })

  // reorganise properties
  delete cleaned.PartitionKey
  delete cleaned.Timestamp
  cleaned.id = cleaned.RowKey
  delete cleaned.RowKey
  delete cleaned['.metadata']

  return cleaned
}

// get entities into a suitable format for submission to the storage service
const azurify = (entity) => {
  const azurified = {}

  forOwn(schema, (attr, name) => {
    // nulls don't exist in table storage - so leave out of task
    if(isUndefined(entity[name]) || (entity[name] === null))
      return

    // observe table storage type overrides
    const explicitType = attr.tableStorageType
    if(explicitType) {
      azurified[name] = eg[explicitType](entity[name])
      return
    }

    switch(attr.type) {
      case 'string':
        azurified[name] = eg.String(entity[name])
        break

      case 'number':
        // craftily store as an int if step = 1
        if(attr.step === 1)
          azurified[name] = eg.Int64(entity[name])
        else
          azurified[name] = eg.Double(entity[name])
        break

      case 'boolean':
        azurified[name] = eg.Boolean(entity[name])
        break

      default:
        throw `er, either the schema is invalid or aftr is broken!?`
    }
  })

  return azurified
}

// throw with a template
const serviceError = (err) => {
  throw `table storage service error: ${err}`
}


// --- exports

export const storageSetup = (_table, _partition, _schema, _limit, connectionString, _blobContainer) => {
  svc = storage.createTableService(connectionString)
  svcBlob = storage.createBlobService(connectionString)

  eg = storage.TableUtilities.entityGenerator

  table = _table
  partition = _partition
  schema = _schema
  limit = _limit
  blobContainer = _blobContainer
}


export const getOnePartitioned = (_partition, rowKey) => {
  return new Promise((resolve) => {
    svc.retrieveEntity(table, _partition, rowKey, (err, result, qRes) => {
      if(qRes.statusCode === 404) {
        resolve({ status: 404, errors: [`not found`] })
      }
      else {
        if(err)
          serviceError(err)
        resolve({ results: deazurify(result) })
      }
    })
  })
}

export const getOne = (rowKey) => getOnePartitioned(partition, rowKey)


export const getAll = (from, order, filter) => {
  const query = new storage.TableQuery()
    .where('PartitionKey eq ?', partition)

  return new Promise((resolve) => {
    svc.queryEntities(table, query, null, (err, queryResult, response) => {
      if(err)
        serviceError(err)

      let results = queryResult.entries

      if(filter) {
        const fails = []
        forOwn(filter, (constraint, attr) => {
          if(!schema[attr])
            return

          results.forEach((r) => {
            const v = r[attr] ? r[attr]._ : null

            const no = (k) => isUndefined(k)
            const pass =
                 (no(constraint.eq)  || (v === constraint.eq))
              && (no(constraint.ne)  || (v !== constraint.ne))
              && (no(constraint.lt)  || (v <   constraint.lt))
              && (no(constraint.gt)  || (v >   constraint.gt))
              && (no(constraint.lte) || (v <=  constraint.lte))
              && (no(constraint.gte) || (v >=  constraint.gte))

            if(! pass)
              fails.push(r.RowKey._)
          })
        })

        results = results.filter((r) => fails.indexOf(r.RowKey._) === -1)
      }

      if(order) {
        results.sort((a, b) => {
          let result = 0

          order.forEach((attr) => {
            if(result !== 0)
              return

            const desc = (attr[0] === '!')
            const actualAttr = attr.replace('!', '')

            let aAttr = (a[actualAttr] || {})._
            let bAttr = (b[actualAttr] || {})._

            // another hack for Int64 types
            if((schema[actualAttr] || {}).type === 'number') {
              aAttr = Number(aAttr)
              bAttr = Number(bAttr)
            }

            if(aAttr === bAttr)
              return
            if(isUndefined(aAttr)) {
              result = desc ? 1 : -1
              return
            }
            if(aAttr < bAttr)
              result = desc ? 1 : -1
            else
              result = desc ? -1 : 1
          })

          return result
        })
      }

      if(limit) {
        let sliced = false
        for(let i = 0; i < results.length; i++) {
          const r = results[i]
          if(r.RowKey._ === from) {
            results = results.slice(i, i+limit)
            sliced = true
            break
          }
        }

        // if we can't find the 'from' id, slice from the beginning
        if(!sliced)
          results = results.slice(0, limit)
      }

      results = results.map(deazurify)
      resolve({ results })
    })
  })
}

export const uploadBlobs = async (entity, originalEntity = {}) => {
  const toUpload = []
  const errors = []

  // not using forOwn due to upcoming 'const mime = await ...'
  for(const name in entity) {
    if(! entity.hasOwnProperty(name))
      continue
    const attr = entity[name]

    // ignore non-blob fields
    if(! schema[name].blobStorage)
      continue

    // if this field isn't being updated, leave it be
    if(attr === originalEntity[name])
      continue

    // don't care if valid base64; will just be corrupt file otherwise.
    // (nb. mimeTypes might root out the problem)
    const raw = Buffer.from(attr, 'base64')
    const ft = fileType(raw)
    const mime =
      ft
      ? ft.mime
      : 'application/octet-stream'

    let whitelist = schema[name].mimeTypes
    // for .includes below
    if(typeof whitelist === 'string')
      whitelist = [whitelist]

    if(whitelist && ! whitelist.includes(mime))
      errors.push(`'${mime}' not an allowed MIME type for '${name}'`)

    if(errors.length)
      continue

    // overwrite original id, if it's present as the original value
    const id = originalEntity[name] || uuid()

    toUpload.push({ attrName: name, id, raw, mime })
  }

  if(errors.length)
    throw ({
      status: 422,
      errors,
    })

  // don't upload in the same loop in order to collect mime errors
  const promises = []
  toUpload.forEach((upload) => {
    promises.push(new Promise((resolve, reject) => {

      const options = { contentSettings: { contentType: upload.mime } }
      svcBlob.createBlockBlobFromText(blobContainer, upload.id, upload.raw, options, (err, blob) => {
        if(err) {
          // internal error - log, don't reveal to client
          console.error(err)
          reject({ status: 500 })
        }

        entity[upload.attrName] = upload.id
        resolve()
      })
    }))
  })

  if(promises.length) {
    try {
      await Promise.all(promises)
    }
    catch(e) {
      // nothing else to do here
      throw e
    }
  }
}

export const addOrReplace = (id, entity) => {
  const replace = !!id
  if(!id)
    id = uuid()

  const task = {
    PartitionKey: eg.String(partition),
    RowKey: eg.String(id),
    ...azurify(entity),
  }

  return new Promise((resolve) => {
    const callback = (err, qRes) => {
      if(err)
        serviceError(err)
      const entityWithId = cloneDeep(entity)
      entityWithId.id = id
      resolve({
        status: replace ? 200 : 201,
        results: entityWithId
      })
    }

    if(!replace)
      svc.insertEntity(table, task, callback)
    else
      svc.replaceEntity(table, task, callback)
  })
}

export const addOne = (entity) =>
  addOrReplace(null, entity)

export const updateOne = (id, entity) =>
  addOrReplace(id, entity)

export const deleteOne = (id) => {
  const task = {
    PartitionKey: { _: partition },
    RowKey: { _: id },
  }

  return new Promise((resolve) => {
    svc.deleteEntity(table, task, (err, qRes) => {
      if(qRes.statusCode === 404) {
        resolve({
          status: 404,
          errors: [`not found`],
        })
      }
      else {
        if(err)
          serviceError(err)

        resolve()
      }
    })
  })
}


export const _test = {
  deazurify,
  azurify,
  serviceError,
}



// WEBPACK FOOTER //
// ./src/storage.js